import { NETWORKS_DATA, VALID_CHAIN_IDS, WALLETS, WALLETS_ARRAY } from '@store/web3Store/web3Store.variables';
import { ISubmenu } from 'src/components/navbar/Sublinks/Sublinks.type';
import { EIcon } from 'src/styles/theme/components/NGIcon/NGIcon.enum';
import { ESubmenuType } from 'src/styles/theme/utils/enums';
import { IAppLink } from '../types/global';

// the right list of items in the navbar
const APP_SUB_MENUS: {
	home: {
		overview: ISubmenu;
	};
	devhub: {
		quests: ISubmenu;
		campaigns: ISubmenu;
	};
	character: {
		overview: ISubmenu;
		forge: ISubmenu;
		chests: ISubmenu;
		inventory: ISubmenu;
	};
	journal: {
		statistics: ISubmenu;
		questLog: ISubmenu;
	};
	staking: {
		protocols: ISubmenu;
	};
	settings: {
		overview: ISubmenu;
	};
	support: {
		faq: ISubmenu;
		aboutUs: ISubmenu;
	};
} = {
	home: {
		overview: {
			id: 'home',
			type: ESubmenuType.homeOverview,
			label: 'Home',
			icon: EIcon.overview2
		}
	},
	devhub: {
		campaigns: {
			id: 'devhub-campaigns',
			type: ESubmenuType.devhubCampaigns,
			label: 'Campaigns',
			icon: EIcon.campaign
		},
		quests: {
			id: 'devhub-quests',
			type: ESubmenuType.devhubQuests,
			label: 'Standalone',
			icon: EIcon.quests2
		}
	},
	character: {
		overview: {
			id: 'character',
			type: ESubmenuType.characterOverview,
			label: 'Overview',
			icon: EIcon.overview2
		},
		inventory: {
			id: 'character-inventory',
			type: ESubmenuType.characterInventory,
			label: 'Inventory',
			icon: EIcon.avatar
		},
		forge: {
			id: 'character-forge',
			type: ESubmenuType.characterForge,
			label: 'Forge',
			icon: EIcon.anvil
		},
		chests: {
			id: 'character-chests',
			type: ESubmenuType.characterChests,
			label: 'Chests',
			icon: EIcon.inventory
		}
	},
	journal: {
		statistics: {
			id: 'statistics',
			type: ESubmenuType.journalStatistics,
			label: 'Statistics',
			icon: EIcon.journal
		},
		questLog: {
			id: 'quest-log',
			type: ESubmenuType.journalQuestLog,
			label: 'Quest Log',
			icon: EIcon.quests2
		}
	},
	staking: {
		protocols: {
			id: 'protocols',
			type: ESubmenuType.stakingProtocols,
			label: 'Protocols',
			icon: EIcon.protocols
		}
	},
	settings: {
		overview: {
			id: 'settings',
			type: ESubmenuType.settingsOverview,
			label: 'Settings',
			icon: EIcon.settings
		}
	},
	support: {
		faq: {
			id: 'support-faq',
			type: ESubmenuType.supportFaq,
			label: 'Faq',
			icon: EIcon.faq
		},
		aboutUs: {
			id: 'support-aboutUs',
			type: ESubmenuType.supportABoutUs,
			label: 'About Us',
			icon: EIcon.nodeguardians
		}
	}
};

const APP_LINKS: Record<string, IAppLink> = {
	home: {
		id: 'home',
		label: 'Home',
		href: '/',
		submenus: Object.values(APP_SUB_MENUS.home),
		isHidden: true
	},
	staking: {
		id: 'staking',
		label: 'Protocols',
		href: '/protocols',
		submenus: Object.values(APP_SUB_MENUS.staking),
		isHidden: true
	},
	devhub: {
		id: 'devhub',
		label: 'Quests',
		href: '/dev-hub',
		submenus: Object.values(APP_SUB_MENUS.devhub)
	},
	singleQuest: {
		id: 'single-quest',
		label: 'Single Quest',
		href: '/dev-hub/quests/[slug]'
	},
	singleCampaign: {
		id: 'single-campaign',
		label: 'Single Campaign',
		href: '/dev-hub/campaigns/[slug]'
	},
	character: {
		id: 'character',
		label: 'Character',
		href: '/character',
		submenus: Object.values(APP_SUB_MENUS.character)
	},
	journal: {
		id: 'journal',
		label: 'Journal',
		href: '/journal',
		submenus: Object.values(APP_SUB_MENUS.journal)
	},
	settings: {
		id: 'settings',
		label: 'Settings',
		href: '/settings',
		submenus: Object.values(APP_SUB_MENUS.settings),
		isHidden: true
	},
	support: {
		id: 'support',
		label: 'Support',
		href: '/support',
		submenus: Object.values(APP_SUB_MENUS.support),
		isHidden: true,
		isMobileVisible: true
	},
	auth: {
		id: 'auth',
		label: 'Auth',
		href: '/auth',
		isHidden: true
	},
	authCli: {
		id: 'auth-cli',
		label: 'Auth Cli',
		href: '/auth/cli',
		isHidden: true
	}
};

const APP_FIRST_SUBPAGES: string[] = [
	APP_SUB_MENUS.devhub.campaigns.id,
	APP_SUB_MENUS.character.overview.id,
	APP_SUB_MENUS.journal.statistics.id,
	APP_SUB_MENUS.staking.protocols.id,
	APP_SUB_MENUS.settings.overview.id,
	APP_SUB_MENUS.support.aboutUs.id
];

const APP_LINKS_ARRAY: IAppLink[] = Object.values(APP_LINKS);

const NAVBAR_LINKS: IAppLink[] = [
	APP_LINKS.home,
	APP_LINKS.devhub,
	APP_LINKS.character,
	APP_LINKS.journal,
	APP_LINKS.staking,
	APP_LINKS.settings,
	APP_LINKS.support
];

const URL_PARAMS = {
	// subpages
	subpage: 's',
	// filter to apply in a subpage
	filters: 'f',
	// quest selected in the quest page
	selectedQuest: 'sq',
	// staking selected in the quest page
	selectedStaking: 'ss',
	// campaign selected in the campaign page
	selectedCampaign: 'sc',
	// quest selected in a campaign the campaign page
	selectedQuestInCampaign: 'sqc',
	// faq selected in the faq page
	selectedFaq: 'sf',
	// avatar customization
	avatarCustomization: 'c'
};

const SUPER_USERS = [
	61942008, // JT
	7825509, // HR
	45238502, // NP
	97748574, // JP
	72504548, // BW
	101326223, // JA
	107807605 // JK
];

const DEFAULT_TRANSITION_TIMEOUT = {
	100: {
		appear: 0,
		enter: 100,
		exit: 100
	},
	200: {
		appear: 0,
		enter: 200,
		exit: 200
	},
	300: {
		appear: 0,
		enter: 300,
		exit: 300
	},
	400: {
		appear: 0,
		enter: 400,
		exit: 400
	}
};

// regex used to extract the language from the code
const LANGUAGE_REGEX = /^\s*#lang:(.*?)#/;

// the key used to save the server state in localStorage
const QUERY_CACHE_KEY = 'query-cache';

// update the trigger a logout, to use in case of breaking changes
const STORAGE_VERSION = `${process.env.BUILD_ID}` || '3';

export {
	APP_FIRST_SUBPAGES,
	APP_LINKS,
	APP_LINKS_ARRAY,
	APP_SUB_MENUS,
	DEFAULT_TRANSITION_TIMEOUT,
	LANGUAGE_REGEX,
	NAVBAR_LINKS,
	NETWORKS_DATA,
	QUERY_CACHE_KEY,
	STORAGE_VERSION,
	SUPER_USERS,
	URL_PARAMS,
	VALID_CHAIN_IDS,
	WALLETS,
	WALLETS_ARRAY
};
